import { ModalLayer } from '@app/components/Molecules/LayersControl'
import type { DialogData, HazardDetail, MapLayer, MapLayerType, RiskWorkspace } from '@app/types'
import { snakeCaseToTitleCase } from '@app/utils/strings.utils'
import { z } from 'zod'

export const workspaceDisplayAuthor = (savestate: RiskWorkspace) => {
  if (savestate.author.name) {
    return savestate.author.name
  }
  const email = savestate.author.email
  const atIdx = email.indexOf('@')
  return atIdx > 0 ? email.slice(0, atIdx) : email
}

export const workspaceToModalLayers = (
  workspace: RiskWorkspace,
  layerData: DialogData,
): ModalLayer[] => {
  const { elementsData, scenariosData, contextsData } = layerData
    ? getIndexedLayers(layerData)
    : { elementsData: {}, scenariosData: {}, contextsData: {} }
  const populatedLayers = {
    ...getCurrentElements(elementsData),
    ...getCurrentContexts(contextsData),
    ...getCurrentHazards(scenariosData),
  }
  const modalLayers: ModalLayer[] = [
    {
      id: 'asset' as MapLayerType,
      layers: workspace.layers.assets.flatMap((id) =>
        populatedLayers[id] ? [populatedLayers[id]] : [],
      ),
    },
    {
      id: 'hazard' as MapLayerType,
      layers: workspace.layers.scenarios.flatMap((id) =>
        populatedLayers[id] ? [populatedLayers[id]] : [],
      ),
    },
    {
      id: 'information' as MapLayerType,
      layers: workspace.layers.contexts.flatMap((id) =>
        populatedLayers[id] ? [populatedLayers[id]] : [],
      ),
    },
  ].filter((modalLayer) => modalLayer.layers.length > 0)
  return modalLayers
}

export const workspaceHasNoValidLayers = (
  workspace: RiskWorkspace,
  layerData: DialogData,
): boolean => {
  const validLayers = workspaceToModalLayers(workspace, layerData)
  return validLayers.length === 0
}

// Below four functions are copied from those in Stories.utils.tsx

export const getCurrentElements = (elementsData: {
  [key: string]: MapLayer
}): { [key: string]: MapLayer } => {
  return (
    Object.entries(elementsData)?.reduce(
      (acc, [layerId, mapLayer]) => {
        acc[layerId] = {
          ...mapLayer,
          visible: true,
          interactivityDisabled: false,
          layerType: 'asset' as MapLayerType, // or hazard or information
          id: mapLayer.tilesets?.[0]?.id,
        }
        return acc
      },
      {} as { [key: string]: MapLayer },
    ) ?? {}
  )
}

export const getCurrentHazards = (scenariosData: {
  [key: string]: MapLayer
}): { [key: string]: MapLayer } => {
  return (
    Object.entries(scenariosData)?.reduce(
      (acc, [layerId, mapLayer]) => {
        acc[layerId] = {
          ...mapLayer,
          display_name: mapLayer.display_name
            ? mapLayer.display_name
            : snakeCaseToTitleCase(mapLayer.assetTag),
          legend: mapLayer.legend,
          visible: true,
          interactivityDisabled: false,
          icon: mapLayer.icon ?? 'Warning',
          layerType: 'hazard',
          id: mapLayer.tilesets?.[0]?.id,
        }
        return acc
      },
      {} as { [key: string]: MapLayer },
    ) ?? {}
  )
}
export const getCurrentContexts = (contextsData: {
  [key: string]: MapLayer
}): { [key: string]: MapLayer } => {
  return (
    Object.entries(contextsData)?.reduce(
      (acc, [layerId, mapLayer]) => {
        acc[layerId] = {
          ...mapLayer,
          visible: true,
          interactivityDisabled: false,
          layerType: 'information',
          id: mapLayer.tilesets?.[0]?.id,
        }
        return acc
      },
      {} as { [key: string]: MapLayer },
    ) ?? {}
  )
}

// Index layers data
export const getIndexedLayers = (layerDialog: DialogData) => {
  // Collect scenarios by their assetTags
  const scenariosData: { [key: string]: MapLayer } = {}
  layerDialog.hazardDialogData.hazardDetails.forEach((hazard: HazardDetail) => {
    hazard.scenarios.forEach((scenario) => {
      scenariosData[scenario.assetTag] = scenario
    })
  })

  // Collect elements by their ids
  const elementsData: { [key: string]: MapLayer } = {}
  Object.values(layerDialog.assetDialogData).forEach((domain) => {
    domain.groups.forEach((group) => {
      group.assets.forEach((element) => {
        elementsData[element.type] = element
      })
    })
  })

  // Collect contexts by their ids
  const contextsData: { [key: string]: MapLayer } = {}
  Object.values(layerDialog.informationDialogData).forEach((menu) => {
    menu.groups.forEach((group) => {
      group.layers.forEach((layer) => {
        contextsData[layer.id] = layer
      })
    })
  })
  return { elementsData, scenariosData, contextsData }
}

export const WorkspaceSchema = z.object({
  name: z.string().min(1, 'Name is required').max(40, 'Name must be 40 characters or fewer'),
  description: z.string().max(200, 'Description must be 200 characters or fewer').optional(),
  visibility: z.enum(['private', 'organisation']),
})

export type WorkspaceFormData = z.infer<typeof WorkspaceSchema>

export const workspaceMatchesMapLayers = (
  workspace: RiskWorkspace,
  mapLayers: MapLayer[],
): boolean => {
  const getLayersKey = (layers: MapLayer[]) => {
    const elementLayers = layers
      .filter((layer) => layer.layerType === 'asset')
      .map((layer) => layer.type)

    const hazardLayers = layers
      .filter((layer) => layer.layerType === 'hazard')
      .map((layer) => layer.assetTag)

    const informationLayers = layers
      .filter((layer) => layer.layerType === 'information')
      .map((layer) => layer.id)

    return [...elementLayers, ...hazardLayers, ...informationLayers].sort().join('|')
  }

  const workspaceLayersKey = Object.values(workspace.layers).flat().sort().join('|')
  return getLayersKey(mapLayers) === workspaceLayersKey
}

export const workspaceLayersExistInDialogData = (
  workspace: RiskWorkspace,
  dialogData: DialogData,
) => {
  const { elementsData, scenariosData, contextsData } = getIndexedLayers(dialogData)

  const allLayerIds = [
    ...workspace.layers.assets,
    ...workspace.layers.scenarios,
    ...workspace.layers.contexts,
  ]

  return allLayerIds.every((layerId) => {
    return (
      elementsData[layerId] !== undefined ||
      scenariosData[layerId] !== undefined ||
      contextsData[layerId] !== undefined
    )
  })
}
